import {Status} from '@github-ui/mergebox/helpers/mergeability-status'
import type {ActionabilityContext, ActionabilityState} from './actionability-types'

/**
 * Maps a `mergeabilityStatus` result plus viewer context into the banner's
 * `ActionabilityState`.
 *
 * Returns `null` when no banner should be shown (terminal states, unknown,
 * or non-actionable failures).
 */
export function deriveActionability(ctx: ActionabilityContext): ActionabilityState | null {
  const {status, authorLogin, viewerLogin, viewerIsRequestedReviewer} = ctx
  const viewerIsAuthor = viewerLogin != null && viewerLogin === authorLogin

  switch (status) {
    // ── Terminal / non-actionable → no banner ──────────────────────────
    case Status.Merged:
    case Status.Closed:
    case Status.NonactionableFailure:
    case Status.Unknown:
      return null

    // ── Draft ──────────────────────────────────────────────────────────
    case Status.Draft:
      return {
        category: 'draft',
        message: viewerIsAuthor
          ? "Still building? Mark as ready for review when you're done."
          : `${authorLogin} is still building — check back later.`,
        actionLabel: viewerIsAuthor ? 'Ready for review' : 'View progress',
        actionTarget: viewerIsAuthor
          ? {type: 'action', actionId: 'mark_ready_for_review'}
          : {type: 'link', urlKey: 'changes'},
      }

    // ── Merge queue ────────────────────────────────────────────────────
    case Status.InMergeQueue:
      return {
        category: 'merging',
        message: viewerIsAuthor
          ? 'Your pull request is in the merge queue.'
          : `${authorLogin}'s pull request is queued.`,
        actionLabel: 'View queue',
        actionTarget: {type: 'link', urlKey: 'overview'},
      }

    // ── Checks failing ─────────────────────────────────────────────────
    case Status.ChecksFailing:
      return {
        category: viewerIsAuthor ? 'fix' : 'waiting',
        message: viewerIsAuthor
          ? 'Checks are failing. Push a fix to continue.'
          : `Checks are failing. Waiting on ${authorLogin}.`,
        actionLabel: 'View checks',
        actionTarget: {type: 'link', urlKey: 'checks'},
      }

    // ── Checks pending ─────────────────────────────────────────────────
    case Status.ChecksPending:
      return {
        category: 'waiting',
        message: 'Checks are still running.',
        actionLabel: 'View checks',
        actionTarget: {type: 'link', urlKey: 'checks'},
      }

    // ── Awaiting review (viewer-aware split) ───────────────────────────
    case Status.AwaitingReview:
      if (viewerIsRequestedReviewer && viewerLogin) {
        return {
          category: 'review',
          message: 'You were requested to review.',
          actionLabel: 'Review changes',
          actionTarget: {type: 'link', urlKey: 'changes'},
        }
      }
      return {
        category: 'waiting',
        message: viewerIsAuthor ? 'Waiting on reviews.' : `Waiting on reviews for ${authorLogin}.`,
        actionLabel: 'View reviewers',
        actionTarget: {type: 'link', urlKey: 'changes'},
      }

    // ── Changes requested ──────────────────────────────────────────────
    case Status.ChangesRequested:
      return {
        category: viewerIsAuthor ? 'fix' : 'waiting',
        message: viewerIsAuthor
          ? 'Changes were requested. Push updates to address feedback.'
          : `Changes requested. Waiting on ${authorLogin}.`,
        actionLabel: 'View feedback',
        actionTarget: {type: 'link', urlKey: 'changes'},
      }

    // ── Merge conflicts ────────────────────────────────────────────────
    case Status.MergeConflicts:
      return {
        category: viewerIsAuthor ? 'fix' : 'waiting',
        message: viewerIsAuthor
          ? 'Resolve merge conflicts to continue.'
          : `Merge conflicts. Waiting on ${authorLogin}.`,
        actionLabel: viewerIsAuthor ? 'Resolve conflicts' : 'View details',
        actionTarget: {type: 'link', urlKey: 'overview'},
      }

    // ── Ready to merge ─────────────────────────────────────────────────
    case Status.Mergeable:
    case Status.NonRequiredChecksUnsuccessful:
      return {
        category: 'ready',
        message: viewerIsAuthor
          ? 'All requirements have been met. Ready to merge!'
          : `All requirements have been met. Waiting on ${authorLogin} to merge.`,
        actionLabel: 'Merge',
        actionTarget: {type: 'action', actionId: 'open_mergebox'},
      }

    // ── Unable to merge (catch-all for remaining blocking states) ──────
    case Status.UnableToMerge:
      return {
        category: viewerIsAuthor ? 'fix' : 'waiting',
        message: viewerIsAuthor
          ? "This pull request can't be merged yet."
          : `Waiting on ${authorLogin} to resolve issues.`,
        actionLabel: 'View details',
        actionTarget: {type: 'link', urlKey: 'overview'},
      }

    default:
      return null
  }
}
